import { createBrowserRouter, RouterProvider, Outlet } from 'react-router-dom'
import { Suspense } from 'react'
import { DashboardLayout } from '@shared/components/layout'
import { Skeleton } from '@shared/components/ui/skeleton'
import { ProtectedRoute, AuthProvider } from '@shared/components/auth'
import { ErrorBoundary } from '@shared/components/ErrorBoundary'
import { ChunkErrorBoundary } from '@shared/components/ChunkErrorBoundary'
import { NotFoundPage } from './routing/lazy'
import { authRoutes, dashboardRoutes, fullPageRoutes, publicRoutes } from './routing/routes.config'

/**
 * Unified layout wrapper component for protected routes with dashboard layout
 */
const ProtectedLayoutWrapper = () => (
  <ProtectedRoute>
    <DashboardLayout>
      <ErrorBoundary inline>
        <ChunkErrorBoundary>
          <Suspense
            fallback={
              <div className="flex h-64 items-center justify-center">
                <Skeleton.Page layout="dashboard" />
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </ChunkErrorBoundary>
      </ErrorBoundary>
    </DashboardLayout>
  </ProtectedRoute>
)

/**
 * Protected routes without sidebar layout (for wizards and full-page forms)
 */
const ProtectedFullPageWrapper = () => (
  <ProtectedRoute>
    <ErrorBoundary inline>
      <ChunkErrorBoundary>
        <Suspense
          fallback={
            <div className="flex h-screen items-center justify-center">
              <Skeleton.Page layout="dashboard" />
            </div>
          }
        >
          <Outlet />
        </Suspense>
      </ChunkErrorBoundary>
    </ErrorBoundary>
  </ProtectedRoute>
)

/**
 * Router configuration with nested layouts
 *
 * Routes are organized into three main groups:
 * 1. authRoutes: Public authentication routes (login, etc.)
 * 2. dashboardRoutes: Protected routes with sidebar layout
 * 3. fullPageRoutes: Protected routes without sidebar (wizards, creation flows)
 *
 * All route definitions are externalized to routing/routes.config.tsx for better maintainability
 */
const router = createBrowserRouter([
  // Authentication routes (no layout)
  ...authRoutes,

  // Protected routes with dashboard layout
  {
    element: <ProtectedLayoutWrapper />,
    children: dashboardRoutes,
  },

  // Protected full-page routes (no sidebar)
  {
    element: <ProtectedFullPageWrapper />,
    children: fullPageRoutes,
  },

  // Public customer-facing routes (no auth, no sidebar)
  ...publicRoutes.map((route) => ({
    ...route,
    element: (
      <ErrorBoundary inline>
        <ChunkErrorBoundary>
          <Suspense
            fallback={
              <div className="flex h-screen items-center justify-center">
                <Skeleton.Page layout="dashboard" />
              </div>
            }
          >
            {route.element}
          </Suspense>
        </ChunkErrorBoundary>
      </ErrorBoundary>
    ),
  })),

  // 404 catch-all route
  {
    path: '*',
    element: (
      <Suspense
        fallback={
          <div className="flex h-screen items-center justify-center">
            <Skeleton.Page layout="dashboard" />
          </div>
        }
      >
        <NotFoundPage />
      </Suspense>
    ),
  },
])

/**
 * Main application router component
 */
export const AppRouter = () => (
  <AuthProvider>
    <RouterProvider router={router} />
  </AuthProvider>
)

export default AppRouter
